import Icon from 'components/Icon';
import { ICONS } from 'consts/consts';
import React, { FC, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  PopoverBody,
  UncontrolledPopover,
} from 'reactstrap';
import { type CustomSurveyStatus } from './consts';

interface Props {
  surveyId: number;
  isOwnerOrAdmin: boolean;
  status: CustomSurveyStatus;
  surveyType: string;
  hasQuestions: boolean;
  hasInstances: boolean;
  hasResponses: boolean;
  onEditClick: (surveyId: number) => void;
  onViewResponsesClick: (surveyId: number) => void;
  onManageAdministratorsClick: (surveyId: number) => void;
  onArchiveClick: (surveyId: number) => void;
  onSetLiveClick: (surveyId: number) => void;
  onDeleteClick: (surveyId: number) => void;
}

const CustomSurveyActionsDropdown: FC<Props> = ({
  surveyId,
  isOwnerOrAdmin,
  status,
  surveyType,
  hasQuestions,
  hasInstances,
  hasResponses,
  onEditClick,
  onViewResponsesClick,
  onManageAdministratorsClick,
  onArchiveClick,
  onSetLiveClick,
  onDeleteClick,
}) => {
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const handleEditClick = useCallback(() => {
    onEditClick(surveyId);
  }, [surveyId, onEditClick]);

  const handleViewResponsesClick = useCallback(() => {
    onViewResponsesClick(surveyId);
  }, [surveyId, onViewResponsesClick]);

  const handleManageAdministratorsClick = useCallback(() => {
    onManageAdministratorsClick(surveyId);
  }, [surveyId, onManageAdministratorsClick]);

  const handleArchiveClick = useCallback(() => {
    onArchiveClick(surveyId);
  }, [surveyId, onArchiveClick]);

  const handleSetLiveClick = useCallback(() => {
    onSetLiveClick(surveyId);
  }, [surveyId, onSetLiveClick]);

  const handleDeleteClick = useCallback(() => {
    onDeleteClick(surveyId);
  }, [surveyId, onDeleteClick]);

  const isReactivationBlocked =
    status === 'ARCHIVED' && surveyType === 'ONE_TIME' && hasInstances;

  // The Archive item always targets ARCHIVED, Set live always targets LIVE -
  // so only one of these two names is ever actually needed per render.
  const isArchiveAction = status === 'LIVE' || status === 'DRAFT';
  const targetStatusName = isArchiveAction
    ? formatMessage({
        id: 'app.views.administration.custom_surveys.surveys.status.archived',
        defaultMessage: 'Archived',
      })
    : formatMessage({
        id: 'app.views.administration.custom_surveys.surveys.status.live',
        defaultMessage: 'Live',
      });

  const isDeleteButtonDisabled =
    !isOwnerOrAdmin || hasResponses || hasInstances;

  return (
    <div className="d-flex align-items-center">
      <button
        className="btn btn-sm btn-light p-1 px-2 me-1"
        onClick={handleEditClick}
        disabled={!isOwnerOrAdmin}
        aria-label={formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.actions.edit_aria',
          defaultMessage: 'Edit survey',
        })}
      >
        <Icon icon={ICONS.EDIT} />
      </button>

      <button
        className="btn btn-sm btn-light p-1 px-2 me-1"
        onClick={handleViewResponsesClick}
        disabled={!hasInstances}
        aria-label={formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.actions.view_responses_aria',
          defaultMessage: 'View responses',
        })}
      >
        <Icon icon={ICONS.BAR_CHART_2} />
      </button>

      <Dropdown isOpen={isOpen} toggle={toggle} direction="end">
        <DropdownToggle
          tag="button"
          className="focusable-element"
          aria-label={formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.actions.aria_label',
            defaultMessage: 'More actions',
          })}
        >
          <span className="btn btn-sm btn-light p-1 px-2">
            <Icon icon={ICONS.MORE_VERTICAL} />
          </span>
        </DropdownToggle>
        <DropdownMenu className="pt-2 pb-2" end>
          {status === 'LIVE' || status === 'DRAFT' ? (
            <span id={`archive-btn-${surveyId}`}>
              <DropdownItem
                onClick={handleArchiveClick}
                disabled={!isOwnerOrAdmin || !hasQuestions}
              >
                <Icon className="pe-2" icon={ICONS.ARCHIVE} />
                <FormattedMessage
                  id="app.views.administration.custom_surveys.surveys.actions.archive"
                  defaultMessage="Archive"
                />
              </DropdownItem>
              {!hasQuestions && (
                <UncontrolledPopover
                  placement="left"
                  trigger="hover"
                  target={`archive-btn-${surveyId}`}
                >
                  <PopoverBody className="text-dark">
                    <FormattedMessage
                      id="app.views.administration.custom_surveys.surveys.editor.status.disabled_no_questions"
                      defaultMessage="Add at least one question before changing the status to {status}."
                      values={{ status: targetStatusName }}
                    />
                  </PopoverBody>
                </UncontrolledPopover>
              )}
            </span>
          ) : (
            <span id={`set-live-btn-${surveyId}`}>
              <DropdownItem
                onClick={handleSetLiveClick}
                disabled={
                  !isOwnerOrAdmin || !hasQuestions || isReactivationBlocked
                }
              >
                <Icon className="pe-2" icon={ICONS.REFRESH} />
                <FormattedMessage
                  id="app.views.administration.custom_surveys.surveys.actions.set_live"
                  defaultMessage="Set live"
                />
              </DropdownItem>
              {isReactivationBlocked ? (
                <UncontrolledPopover
                  placement="left"
                  trigger="hover"
                  target={`set-live-btn-${surveyId}`}
                >
                  <PopoverBody className="text-dark">
                    <FormattedMessage
                      id="app.views.administration.custom_surveys.surveys.actions.set_live.disabled_one_time_already_sent"
                      defaultMessage="This is a one-time survey and has already been sent. It can't be reactivated."
                    />
                  </PopoverBody>
                </UncontrolledPopover>
              ) : (
                !hasQuestions && (
                  <UncontrolledPopover
                    placement="left"
                    trigger="hover"
                    target={`set-live-btn-${surveyId}`}
                  >
                    <PopoverBody className="text-dark">
                      <FormattedMessage
                        id="app.views.administration.custom_surveys.surveys.editor.status.disabled_no_questions"
                        defaultMessage="Add at least one question before changing the status to {status}."
                        values={{ status: targetStatusName }}
                      />
                    </PopoverBody>
                  </UncontrolledPopover>
                )
              )}
            </span>
          )}
          <DropdownItem
            onClick={handleManageAdministratorsClick}
            disabled={!isOwnerOrAdmin}
          >
            <Icon className="pe-2" icon={ICONS.ADD_PERSON} />
            <FormattedMessage
              id="app.views.administration.custom_surveys.surveys.actions.manage_administrators"
              defaultMessage="Manage administrators"
            />
          </DropdownItem>
          <span id={`delete-survey-action-${surveyId}`}>
            <DropdownItem
              className={!isDeleteButtonDisabled ? 'text-danger' : null}
              onClick={handleDeleteClick}
              disabled={isDeleteButtonDisabled}
            >
              <Icon className="pe-2" icon={ICONS.TRASH} />
              <FormattedMessage
                id="app.views.administration.custom_surveys.surveys.actions.delete"
                defaultMessage="Delete"
              />
            </DropdownItem>
          </span>
          {(hasResponses || hasInstances) && (
            <UncontrolledPopover
              placement="left"
              trigger="hover"
              target={`delete-survey-action-${surveyId}`}
            >
              <PopoverBody className="text-dark">
                {hasResponses && status === 'ARCHIVED' ? (
                  <FormattedMessage
                    id="app.views.administration.custom_surveys.surveys.actions.delete_disabled_tooltip_archived"
                    defaultMessage="This survey has responses and cannot be deleted."
                  />
                ) : hasResponses ? (
                  <FormattedMessage
                    id="app.views.administration.custom_surveys.surveys.actions.delete_disabled_tooltip"
                    defaultMessage="This survey has responses and cannot be deleted. You can archive it instead."
                  />
                ) : (
                  <FormattedMessage
                    id="app.views.administration.custom_surveys.surveys.actions.delete_disabled_tooltip_sent"
                    defaultMessage="This survey has already been sent and cannot be deleted."
                  />
                )}
              </PopoverBody>
            </UncontrolledPopover>
          )}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

export default React.memo(CustomSurveyActionsDropdown);
